/**
 * Leaderboard - Ranked list with values
 *
 * Usage:
 *   <Leaderboard
 *     title="Top Repos by Stars"
 *     data={{
 *       items: [
 *         { rank: 1, label: 'react', value: 220000, icon: '⭐' },
 *         { rank: 2, label: 'vue', value: 205000, icon: '⭐' },
 *       ]
 *     }}
 *   />
 */

import type { BlockProps } from './types';
import { CHART_COLORS } from './colors';
import './blocks.css';

export interface LeaderboardData {
  items: Array<{
    rank?: number;
    label: string;
    value: number;
    sublabel?: string;
    icon?: string;
    change?: number; // Position change (+1, -2, etc.)
  }>;
  /** Unit for values */
  unit?: string;
  /** Show bar visualization */
  showBars?: boolean;
}

export interface LeaderboardProps extends BlockProps<LeaderboardData> {
  /** Maximum items to show */
  maxItems?: number;
  /** Click handler */
  onItemClick?: (item: LeaderboardData['items'][0], index: number) => void;
}

export function Leaderboard({
  data,
  title,
  maxItems = 10,
  onItemClick,
  className,
}: LeaderboardProps) {
  const { items, unit, showBars = true } = data;

  if (!items || items.length === 0) {
    return (
      <div className={`block-table ${className || ''}`}>
        {title && <div className="block-table-title">{title}</div>}
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
          No data available
        </div>
      </div>
    );
  }

  const displayItems = items.slice(0, maxItems);
  const maxValue = Math.max(...items.map(i => i.value));

  const getRankStyle = (rank: number) => {
    if (rank === 1) return { background: '#ffd700', color: 'var(--color-text)' }; // Gold
    if (rank === 2) return { background: '#c0c0c0', color: 'var(--color-text)' }; // Silver
    if (rank === 3) return { background: '#cd7f32', color: '#fff' }; // Bronze
    return { background: 'var(--color-surface-muted)', color: 'var(--color-text-muted)' };
  };

  const getChangeDisplay = (change: number | undefined) => {
    if (change === undefined || change === 0) return null;
    const color = change > 0 ? 'var(--color-success)' : 'var(--color-danger)';
    const arrow = change > 0 ? '↑' : '↓';
    return (
      <span style={{ color, fontSize: '11px', fontWeight: 600 }}>
        {arrow}{Math.abs(change)}
      </span>
    );
  };

  return (
    <div className={`block-table ${className || ''}`}>
      {title && <div className="block-table-title">{title}</div>}

      <div style={{ padding: '0' }}>
        {displayItems.map((item, index) => {
          const rank = item.rank ?? index + 1;
          const rankStyle = getRankStyle(rank);
          const barWidth = showBars ? (item.value / maxValue) * 100 : 0;

          return (
            <div
              key={index}
              onClick={onItemClick ? () => onItemClick(item, index) : undefined}
              onKeyDown={onItemClick ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onItemClick(item, index);
                }
              } : undefined}
              tabIndex={onItemClick ? 0 : undefined}
              role={onItemClick ? 'button' : undefined}
              aria-label={onItemClick ? `Rank ${rank}: ${item.label}, ${item.value.toLocaleString()}${unit ? ` ${unit}` : ''}` : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 16px',
                borderBottom: index < displayItems.length - 1 ? '1px solid var(--color-surface-hover)' : 'none',
                cursor: onItemClick ? 'pointer' : undefined,
                transition: 'background 0.1s ease',
                position: 'relative',
              }}
              onMouseEnter={(e) => {
                if (onItemClick) e.currentTarget.style.background = 'var(--color-surface-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {/* Background bar */}
              {showBars && (
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${barWidth}%`,
                  background: CHART_COLORS[0],
                  opacity: 0.08,
                  transition: 'width 0.3s ease',
                }} />
              )}

              {/* Rank badge */}
              <div style={{
                minWidth: '24px',
                height: '24px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 700,
                ...rankStyle,
              }}>
                {rank}
              </div>

              {/* Icon */}
              {item.icon && (
                <span style={{ fontSize: '18px' }}>{item.icon}</span>
              )}

              {/* Label */}
              <div style={{ flex: 1, minWidth: 0, zIndex: 1 }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'var(--color-text)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {item.label}
                </div>
                {item.sublabel && (
                  <div style={{
                    fontSize: '11px',
                    color: 'var(--color-text-muted)',
                  }}>
                    {item.sublabel}
                  </div>
                )}
              </div>

              {/* Change indicator */}
              {getChangeDisplay(item.change)}

              {/* Value */}
              <div style={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--color-text)',
                zIndex: 1,
              }}>
                {item.value.toLocaleString()}
                {unit && <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', marginLeft: '2px' }}>{unit}</span>}
              </div>
            </div>
          );
        })}
      </div>

      {items.length > maxItems && (
        <div style={{
          padding: '8px 16px',
          textAlign: 'center',
          fontSize: '12px',
          color: 'var(--color-text-muted)',
          borderTop: '1px solid var(--color-border)',
        }}>
          +{items.length - maxItems} more
        </div>
      )}
    </div>
  );
}

export default Leaderboard;
